import { Box, CircularProgress, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, styled, tableCellClasses } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Pagination from '../../../components/TablePagination'
import React, { useEffect } from 'react'
import AssignAssetWindow from './AssignAssetWindow'
import { type ConnectedProps, connect } from 'react-redux'
import { type RootState } from '../../../store'
import { fetchConfigurationList } from '../redux/actionCreator'
import { type ConfigurationListState } from '../redux/configurationListSlice'
import { type ConfigurationObj } from '../services/configurationService'
import { changeTemplateId } from '../redux/templateIdSlice'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useAuth } from 'react-oidc-context'
import { type OperationPermissionState } from '../../dashboard/redux/operationPermissionSlice'

const style = {
  width: '99%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0
}

function EnhancedTableHead (props: any): any {
  return (
          <TableHead data-testid='table-header'>
              <TableRow>
                  {headCells.map((headCell: HeadCell) => (
                      <StyledTableCell
                          key={headCell.id}
                          align={'left'}
                          padding={headCell.disablePadding ? 'none' : 'normal'}
                      >
                          <>{headCell.label}</>
                      </StyledTableCell>
                  ))}
              </TableRow>
          </TableHead>
  )
}

interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web'
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'template_name',
    numeric: false,
    disablePadding: false,
    label: 'NAME'
  },
  {
    id: 'category_type_name',
    numeric: false,
    disablePadding: false,
    label: 'CATEGORY'
  },
  {
    id: 'asset_type_name',
    numeric: false,
    disablePadding: false,
    label: 'ASSET TYPE'
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'STATUS'
  },
  {
    id: 'assets_associated',
    numeric: true,
    disablePadding: false,
    label: 'ASSIGNED DEVICES'
  }
]

type Order = 'asc' | 'desc'

interface Sort {
  order: Order
  orderBy: string
}

const ConfigurationTable = (props: PropsFromRedux): JSX.Element => {
  const [configList, setConfigList] = React.useState<ConfigurationObj[]>([])
  const [selectedAssetType, setSelectedAssetType] = React.useState<string>('')
  const [selectedConfiguration, setSelectedConfiguration] = React.useState<string>('')
  const [selected, setSelected] = React.useState<readonly number[]>([])
  const [selectedRow, setSelectedRow] = React.useState<any>(null)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [showModifyAssetList, setShowModifyAssetList] = React.useState('')
  const [page, setPage] = React.useState(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'asc', orderBy: 'template_id' })
  const rowsPerPage = 10
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const auth = useAuth()

  const handleFetchConfigList = (): void => {
    if (page >= 1) {
      props.fetchConfigurationList(page - 1, rowsPerPage, sort.order, sort.orderBy, auth.user?.profile.customer as string)
    }
  }

  useEffect(() => {
    handleFetchConfigList()
  }, [page, sort])

  useEffect(() => {
    if (props.configurationListState.configurationList !== null && props.configurationListState.configurationList.configuration_templates.length > 0) {
      setConfigList(props.configurationListState.configurationList.configuration_templates)
    }
  }, [props.configurationListState.configurationList])

  const handleEllipsisClick = (event: any, row: string): void => {
    setAnchorEl(event.currentTarget)
    setSelectedRow(row)
  }

  const handleRequestSort =
        (event: React.MouseEvent<unknown>, newOrderBy: keyof ConfigurationObj): void => {
          const isAsc = sort.orderBy === newOrderBy && sort.order === 'asc'
          const toggledOrder = isAsc ? 'desc' : 'asc'
          setSort({ order: toggledOrder, orderBy: newOrderBy })
        }

  const handleMenuClose = (): void => {
    setAnchorEl(null)
    setSelectedRow(null)
  }

  const handleRowClick = (templateId: string): void => {
    dispatch(changeTemplateId(templateId))
    sessionStorage.setItem('templateId', templateId)
    navigate('/asset-config-details')
  }

  const handleAssignClick = (assetTypeId: string, templateId: string, modifyOption: string): void => {
    setSelectedAssetType(assetTypeId)
    setSelectedConfiguration(templateId)
    setShowModifyAssetList(modifyOption)
    setSelectedRow(null)
  }

  const onAssetWindowClose = (): void => {
    setShowModifyAssetList('')
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked && configList.length > 0) {
      const newSelected: any = configList.map((n: any) => n.name)
      setSelected(newSelected)
      return
    }
    setSelected([])
  }

  const isSelected = (index: number): boolean => selected.includes(index)

  if (props.configurationListState.configurationList !== null && props.configurationListState.configurationList.configuration_templates.length > 0 && props.configurationListState.httpStatus === 200) {
    return (
      <>
        <Box sx={style} className="devices">
          <Paper
            sx={{
              width: '100%',
              mb: 2,
              backgroundColor: '#272727',
              boxShadow: 'unset',
              color: '#F0F0F0',
              marginBottom: '0'
            }}
          >
            <TableContainer className="device-table">
              <Table
                sx={{ minWidth: 750, color: '#f0f0f0' }}
                aria-labelledby="tableTitle"
                size={'small'}
              >
                <EnhancedTableHead
                  numSelected={selected.length}
                  order={sort.order}
                  orderBy={sort.orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={configList.length}
                />
                <TableBody>
                  {configList.length > 0 &&
                    configList.map((row: ConfigurationObj, index: number) => {
                      const isItemSelected = isSelected(index)
                      const labelId = `enhanced-table-checkbox-${row.template_id}`

                      return (
                        <StyledTableRow
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.template_id}
                          aria-label="devices-list-row"
                          selected={isItemSelected}
                          sx={{ cursor: 'pointer' }}
                        >
                          <StyledTableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            padding="none"
                            aria-label="device-name"
                            onClick={() => { handleRowClick(row.template_id) }}
                            data-testid="device-name"
                            sx={{
                              color: '#64C3FF !important',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '25vw'
                            }}
                          >
                            {row.template_name}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.category_type_name.toUpperCase()}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.asset_type_name}
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{
                              color:
                                row.status.toUpperCase() === 'ACTIVE'
                                  ? '#68D560 !important'
                                  : '#FF644C !important'
                            }}
                          >
                            {row.status}
                          </StyledTableCell>
                          <StyledTableCell>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              {row.assets_associated}
                              { props.operationPermissions.permissions.can_modify_asset_config
                                ? <>
                                  <IconButton
                                    aria-controls={`menu-${index}`}
                                    aria-haspopup="true"
                                    data-testid="device-ellipsis"
                                    onClick={(event) => {
                                      handleEllipsisClick(event, row.template_id)
                                    }}
                                    sx={{ padding: '0px !important' }}
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                  <Menu
                                    id={`menu-${row.template_id}`}
                                    anchorEl={anchorEl}
                                    open={
                                      Boolean(anchorEl) &&
                                      selectedRow === row.template_id
                                    }
                                    onClose={handleMenuClose}
                                  >
                                      <MenuItem
                                        data-testid="assign-btn"
                                        onClick={() => {
                                          handleAssignClick(row.asset_type_id, row.template_id, 'Assign')
                                        }}
                                      >
                                        Assign Asset
                                      </MenuItem>
                                      <MenuItem
                                        data-testid="unassign-btn"
                                        onClick={() => {
                                          handleAssignClick(row.asset_type_id, row.template_id, 'Unassign')
                                        }}
                                      >
                                        Unassign Asset
                                      </MenuItem>
                                    </Menu>
                                  </>
                                : <></>
                              }
                            </div>
                          </StyledTableCell>
                        </StyledTableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination count={props.configurationListState.configurationList.total_count} page={page} setPage={setPage} rowsPerPage={rowsPerPage} siblingCount={1}
                        boundaryCount={1} />
          </Paper>
        </Box>
        {showModifyAssetList !== '' && <AssignAssetWindow handleFetchConfigList={handleFetchConfigList} assetTypeId={selectedAssetType} templateId={selectedConfiguration} onClose={onAssetWindowClose} assetListOption={showModifyAssetList} />}
      </>
    )
  } else if (props.configurationListState.isLoading) {
    return (
      <div className='CircularprogressAmi'>
        <CircularProgress />
      </div>
    )
  } else if (props.configurationListState.httpStatus === 401) {
    return (
            <div className='authError'><AuthError errorMessage="Unauthorized"/></div>
    )
  } else if (props.configurationListState.httpStatus === 403) {
    return (
        <div className='authError'><AuthError errorMessage="accessForbidden"/></div>
    )
  } else if ((props.configurationListState.configurationList === null && props.configurationListState.httpStatus === 200)) {
    return (
        <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
    )
  } else if ((props.configurationListState.configurationList === null && props.configurationListState.error === 'No configuration templates found')) {
    return (
        <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
    )
  } else {
    return (
        <div className='authError'><AuthError errorMessage="cannotFetch" retry={handleFetchConfigList}/></div>
    )
  }
}

interface DispatchToProps {
  fetchConfigurationList: (page: number, size: number, sortdir: string, sortfield: string, tenantId: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchConfigurationList: (page: number, size: number, sortdir: string, sortfield: string, tenantId: string) => dispatch(fetchConfigurationList(page, size, sortdir, sortfield, tenantId))
})

interface StateToProps {
  configurationListState: ConfigurationListState
  operationPermissions: OperationPermissionState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  configurationListState: state.configurationList,
  operationPermissions: state.operationPermissions
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(ConfigurationTable)
